import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Grid, GridCell, Modal, NotificationManagerContext, NotificationInline,
   Spacer, Switch, Text, View } from 'oio-react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { DeleteIcon } from 'assets/icons'
import { adminManagePagesUrl } from 'config/constants/urls'
import CustomAppDeleteConfirm from 'src/apps/CustomApp/components/DeleteConfirm'
import { Input } from 'src/components/ConnectedForm'
import ActionListItemRow from 'src/components/Settings/ActionListItemRow'
import UrlActionListItemRow from 'src/components/UrlActionListItemRow'
import ModalRoute from 'src/components/utils/ModalRoute'
import { useOrganizationWithOptions, usePage,
   useUpdateOrganization, useUpdatePage } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'
import UrlEdit from './UrlEdit'

const CustomPageAbout = () => {
   const history = useHistory()
   const match = useRouteMatch()
   const { pageId } = useParams()
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)
   const [error, setError] = useState(null)
   const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)

   const { page } = usePage({ id: pageId })
   const { updatePage } = useUpdatePage()
   const { organization } = useOrganizationWithOptions({
      withGlobalNavLinksTargetMetadata: true
   })

   const { updateOrganization } = useUpdateOrganization({
      variables: {
         withGlobalNavLinksTargetMetadata: true
      }
   })

   const pageGlobalNavLink = organization.globalNavLinks.find(l => l.page?.id === page.id)

   const handleUpdateCustomPage = async (values, actions) => {
      try {
         await updatePage({ id: page.id }, values)

         // TODO: For now, while pages are pretending to be custom apps,
         // We manually sync updates to globalNavLinks
         // PR #692
         await updateOrganization({
            globalNavLinks: [{
               operation: 'updateInternalLink',
               updateInternalLink: {
                  id: pageGlobalNavLink.id,
                  name: values.title
               }
            }]
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         setError(err)
      } finally {
         actions.setSubmitting(false)
      }
   }

   const handleToggleCustomPage = async (values) => {
      try {
         await updatePage({ id: page.id }, { published: !page.published })

         // TODO: For now, while pages are pretending to be custom apps,
         // We manually sync updates to globalNavLinks
         // PR #692
         // Now, manually create or delete a corresponding globalNavLink
         const operationName = page.published
            ? 'remove'
            : 'createInternalLink'

         const payload = page.published
            ? pageGlobalNavLink.id
            : { name: page.title, pageId: page.id }

         await updateOrganization({
            globalNavLinks: [{
               operation: operationName,
               [operationName]: payload
            }]
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         setError(err)
      }
   }

   return (
      <>
         <View
            borderRadius="3px"
            display="flex"
            width="100%"
            backgroundColor="#fff"
            padding="15px">
            <View flex="1 1 auto" paddingRight="60px">
               <Text size="2" weight="medium">
                  {`This Custom Page is ${page.published ? 'Enabled' : 'Disabled'}`}
               </Text>
               <Spacer size="1" />
               <Text size="1.5" color="#666">
                  {page.published && (
                     'Turn this off if you would like to disable this page. Disabling this page will hide this page and all its contents for all users.'
                  )}
                  {!page.published && (
                     'Turn this on if you would like to enable this page.'
                  )}
               </Text>
            </View>
            <View flex="0 0 auto">
               <Switch
                  highlightColor="#0dd16d"
                  defaultChecked={page.published}
                  onChange={handleToggleCustomPage}
               />
            </View>
         </View>
         <Spacer size="5" />
         <Formik
            initialValues={{ title: page.title }}
            onSubmit={handleUpdateCustomPage}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f3f3f3"
                  onSubmit={handleSubmit}>
                  {error && (
                     <NotificationInline
                        borderRadius="3px"
                        textSize="2"
                        type="error"
                        title="Error"
                        message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                     />
                  )}
                  <View
                     width="100%"
                     padding="15px"
                     borderRadius="3px"
                     backgroundColor="#fff">
                     <Grid spacing="10px">
                        <GridCell colspan="4">
                           <Text size="2" weight="medium">
                              Page Name
                           </Text>
                           <Text size="1.5" color="#999">
                              (max 100 characters)
                           </Text>
                        </GridCell>
                        <GridCell colspan="8">
                           <Input
                              maxLength="100"
                              size="md"
                              name="title"
                              placeholder="Page Name"
                           />
                        </GridCell>
                        <GridCell colspan="12">
                           <View
                              display="flex"
                              justifyContent="flex-end"
                              width="100%"
                              borderTop="1px solid #eee"
                              paddingTop="10px">
                              <Button
                                 type="submit"
                                 name="Save Changes"
                                 mode={isSubmitting ? 'loading' : 'normal'}
                                 color={theme.protoSettingsHighlightBackgroundColor}
                                 textColor={theme.protoSettingsHighlightForegroundColor}
                                 textWeight="medium"
                                 size="sm"
                                 textSize="0.9"
                                 padding="25px"
                              />
                           </View>
                        </GridCell>
                     </Grid>
                  </View>
               </Form>
            )}
         />
         <Spacer size="5" />
         <UrlActionListItemRow
            editUrl={`${match.url}/urls`}
            entityName="Page"
            globalUrl={page.globalUrls[0]}
            slugUrl={`/pages/${page.slug}`}
         />
         <ActionListItemRow
            name="Delete this Custom Page"
            description="Permanently delete this Custom Page. This action cannot be undone."
            action={(
               <Button
                  onClick={() => setDeleteModalIsOpen(true)}
                  width="78px"
                  size="xs"
                  name="Delete"
                  color="#eee"
                  textColor="#df4e34"
               />
            )}
            icon={(
               <DeleteIcon
                  width="18px"
                  height="18px"
                  strokeWidth="2px"
                  color="#aaa"
               />
            )}
         />
         <Modal
            borderRadius="3px"
            width="90%[a-b] 300px[c-f]"
            onCloseTrigger={() => setDeleteModalIsOpen(false)}
            open={deleteModalIsOpen}>
            <CustomAppDeleteConfirm
               pageId={page.id}
               pageTitle={page.title}
               onCancelButtonClick={() => setDeleteModalIsOpen(false)}
               onDelete={() => history.push(adminManagePagesUrl)}
            />
         </Modal>
         <ModalRoute path={`${match.path}/urls`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="3px[c-f]"
                  width="100%[a-b] 360px[c-f]"
                  height="100%[a-b] 450px[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <UrlEdit
                     targetType="page"
                     onCloseButtonClick={onCloseTrigger}
                     onUpdate={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
      </>
   )
}

export default CustomPageAbout
